import apiClient from './api';
import type {
  CalDAVSettingDto,
  CreateCalDAVSettingRequest,
  UpdateCalDAVSettingRequest,
  CalDAVSyncResponse,
  CalDAVTestConnectionResponse,
  CalDAVSyncLogDto,
  SyncTicketToCalDAVRequest,
  CalDAVEventsResponse,
  CreateCalDAVEventRequest,
} from '../types/api';

/**
 * CalDAV Service für Kalendersynchronisation
 */
export const calDAVService = {
  // ===== CalDAV Settings =====

  /**
   * Alle CalDAV-Settings abrufen
   */
  getAllSettings: async (): Promise<CalDAVSettingDto[]> => {
    const response = await apiClient.get<CalDAVSettingDto[]>('/caldav/settings');
    return response.data;
  },

  /**
   * Ein CalDAV-Setting abrufen
   */
  getSetting: async (id: number): Promise<CalDAVSettingDto> => {
    const response = await apiClient.get<CalDAVSettingDto>(`/caldav/settings/${id}`);
    return response.data;
  },

  /**
   * CalDAV-Setting erstellen
   */
  createSetting: async (data: CreateCalDAVSettingRequest): Promise<{ calDAVSettingID: number; message: string }> => {
    const response = await apiClient.post<{ calDAVSettingID: number; message: string }>('/caldav/settings', data);
    return response.data;
  },

  /**
   * CalDAV-Setting aktualisieren
   */
  updateSetting: async (id: number, data: UpdateCalDAVSettingRequest): Promise<{ message: string }> => {
    const response = await apiClient.put<{ message: string }>(`/caldav/settings/${id}`, data);
    return response.data;
  },

  /**
   * CalDAV-Setting löschen
   */
  deleteSetting: async (id: number): Promise<{ message: string }> => {
    const response = await apiClient.delete<{ message: string }>(`/caldav/settings/${id}`);
    return response.data;
  },

  // ===== Verbindungstest =====

  /**
   * CalDAV-Verbindung testen
   */
  testConnection: async (data: CreateCalDAVSettingRequest): Promise<CalDAVTestConnectionResponse> => {
    const response = await apiClient.post<CalDAVTestConnectionResponse>('/caldav/test-connection', data);
    return response.data;
  },

  // ===== Synchronisation =====

  /**
   * Alle Tickets synchronisieren
   */
  syncAllTickets: async (calDAVSettingId: number): Promise<CalDAVSyncResponse> => {
    const response = await apiClient.post<CalDAVSyncResponse>('/caldav/sync', calDAVSettingId);
    return response.data;
  },

  /**
   * Einzelnes Ticket synchronisieren
   */
  syncTicket: async (data: SyncTicketToCalDAVRequest): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>('/caldav/sync-ticket', data);
    return response.data;
  },

  // ===== Sync-Logs =====

  /**
   * Sync-Logs abrufen
   */
  getSyncLogs: async (ticketId?: number, limit?: number): Promise<CalDAVSyncLogDto[]> => {
    const params = new URLSearchParams();
    if (ticketId) params.append('ticketId', ticketId.toString());
    if (limit) params.append('limit', limit.toString());

    const response = await apiClient.get<CalDAVSyncLogDto[]>(`/caldav/sync-logs?${params.toString()}`);
    return response.data;
  },

  // ===== CalDAV Events (Termin-Kalender) =====

  /**
   * Events aus einem CalDAV-Kalender abrufen
   */
  getEvents: async (settingId: number, startDate?: Date, endDate?: Date): Promise<CalDAVEventsResponse> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate.toISOString());
    if (endDate) params.append('endDate', endDate.toISOString());

    const queryString = params.toString();
    const url = queryString ? `/caldav/events/${settingId}?${queryString}` : `/caldav/events/${settingId}`;
    const response = await apiClient.get<CalDAVEventsResponse>(url);
    return response.data;
  },

  /**
   * Neues Event im CalDAV-Kalender erstellen
   */
  createEvent: async (data: CreateCalDAVEventRequest): Promise<{ success: boolean; eventUID?: string; message: string }> => {
    const response = await apiClient.post<{ success: boolean; eventUID?: string; message: string }>('/caldav/events', data);
    return response.data;
  },

  /**
   * Event aus dem CalDAV-Kalender löschen
   */
  deleteEvent: async (settingId: number, eventUid: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete<{ success: boolean; message: string }>(`/caldav/events/${settingId}/${encodeURIComponent(eventUid)}`);
    return response.data;
  },
};

export default calDAVService;
